import { motion } from "framer-motion";
import { ArrowRight, Bot, CheckCircle2, LineChart, Mail, MessageSquare, Radio, Rocket, Shield, Sparkles, Zap, Users, TrendingUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import { useState, useEffect, useRef } from "react";
import { toast } from "sonner";

const stats = [
  { label: "Pipeline generated", value: "$48M+" },
  { label: "Emails sent monthly", value: "3.2M" },
  { label: "Reply-to-demo rate", value: "28%" },
  { label: "Positive reply rate", value: "11.8%" },
];

const features = [
  { icon: Radio, title: "Real-time enrichment", desc: "Live tech stack, revenue, intent signals & mobile — no CSV imports." },
  { icon: Bot, title: "Autonomous AI BDRs", desc: "Persona-tuned agents research, write and respond around the clock." },
  { icon: Zap, title: "7-touch multichannel", desc: "Email + LinkedIn + voice notes orchestrated on one adaptive timeline." },
  { icon: MessageSquare, title: "Unified smart inbox", desc: "AI drafts replies, handles objections, books meetings on your calendar." },
  { icon: LineChart, title: "Pipeline attribution", desc: "Track every meeting back to persona, campaign and channel." },
  { icon: Shield, title: "Deliverability engine", desc: "Domain warmup, bounce checks, inbox rotation baked in." },
];

const touches = [
  { day: "Day 1", channel: "Email", label: "Hyper-personalized intro" },
  { day: "Day 2", channel: "LinkedIn", label: "Connection request" },
  { day: "Day 4", channel: "LinkedIn", label: "Value-first DM" },
  { day: "Day 6", channel: "Email", label: "Case study follow-up" },
  { day: "Day 9", channel: "Email", label: "Break-up nudge" },
  { day: "Day 12", channel: "LinkedIn", label: "Voice note" },
  { day: "Day 16", channel: "Email", label: "Signal-based revive" },
];

const logos = ["CloudScale", "DataForge", "PulseAI", "Northwind", "Meridian", "Vertex", "Findem", "Satchel", "Uniconomic", "Metal", "CloudScale", "DataForge"];

const tiers = [
  {
    name: "Solo",
    price: 250,
    annual: 200,
    lookup_monthly: "solo_monthly",
    lookup_yearly: "solo_yearly",
    tagline: "A sales team of one",
    features: ["200 AI-researched contacts / mo", "1 AI BDR agent", "1 domain · 3 mailboxes · 1 LinkedIn", "Turnkey warmup & deliverability", "HubSpot two-way sync", "Cancel anytime"],
    popular: false,
  },
  {
    name: "Explore",
    price: 900,
    annual: 720,
    lookup_monthly: "explore_monthly",
    lookup_yearly: "explore_yearly",
    tagline: "Prove outbound can fill pipeline",
    features: ["800 AI-researched contacts / mo", "Unlimited users", "2 domains · 6 mailboxes · 5 LinkedIn", "LinkedIn signal tracking", "Dedicated GTM engineer onboarding", "24/7 Slack support"],
    popular: true,
  },
  {
    name: "Scale",
    price: 2500,
    annual: 2000,
    lookup_monthly: "scale_monthly",
    lookup_yearly: "scale_yearly",
    tagline: "Make outbound a channel you can count on",
    features: ["2,500 AI-researched contacts / mo", "Unlimited users", "6 domains · 18 mailboxes · 20 LinkedIn", "Salesforce native sync", "AI videos + LinkedIn voice notes", "Website visitor tracking"],
    popular: false,
  },
];

export default function Landing() {
  const { user, login } = useAuth();
  const [billing, setBilling] = useState("monthly");
  const [loadingTier, setLoadingTier] = useState(null);
  const pricingRef = useRef(null);
  const beaconSent = useRef(false);
  const dwellStart = useRef(null);

  // Inbound signal beacon: fires when pricing section becomes visible.
  useEffect(() => {
    const el = pricingRef.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting && !dwellStart.current) {
            dwellStart.current = Date.now();
          }
          if (!e.isIntersecting && dwellStart.current && !beaconSent.current) {
            const dwell = Math.round((Date.now() - dwellStart.current) / 1000);
            beaconSent.current = true;
            api.post("/signals/pricing-visit", {
              page: "/#pricing",
              referrer: document.referrer || null,
              dwell_seconds: dwell,
            }).catch(() => {});
          }
        });
      },
      { threshold: 0.35 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  const cta = () => (user ? (window.location.href = "/dashboard") : login());

  const subscribe = async (tier) => {
    if (!user) return login();
    setLoadingTier(tier.name);
    try {
      const lookup = billing === "annual" ? tier.lookup_yearly : tier.lookup_monthly;
      const { data } = await api.post("/payments/checkout", {
        lookup_key: lookup,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error("Could not start checkout");
      setLoadingTier(null);
    }
  };

  return (
    <div className="relative min-h-screen bg-[#0A0D14] text-slate-100 overflow-x-hidden">
      {/* Nav */}
      <header className="sticky top-0 z-40 border-b border-white/5 backdrop-blur-xl bg-[#0A0D14]/70">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <a href="#" className="flex items-center gap-2" data-testid="nav-logo">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-sky-400 to-emerald-400 grid place-items-center">
              <Bot className="w-4 h-4 text-slate-900" />
            </div>
            <span className="font-bold tracking-tight text-lg">AiPipe</span>
          </a>
          <nav className="hidden md:flex items-center gap-8 text-sm text-slate-400">
            <a href="#features" className="hover:text-white transition">Product</a>
            <a href="#how" className="hover:text-white transition">How it works</a>
            <a href="#pricing" className="hover:text-white transition">Pricing</a>
            <a href="#proof" className="hover:text-white transition">Customers</a>
          </nav>
          <div className="flex items-center gap-3">
            {user ? (
              <Button size="sm" onClick={() => (window.location.href = "/dashboard")} data-testid="nav-dashboard-btn" className="bg-sky-500 hover:bg-sky-400 text-slate-950 font-medium">
                Dashboard <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            ) : (
              <Button size="sm" onClick={login} data-testid="nav-signin-btn" className="bg-white hover:bg-slate-200 text-slate-900 font-medium rounded-full">
                Sign in
              </Button>
            )}
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative">
        <div className="absolute inset-0 grid-bg opacity-40 pointer-events-none" />
        <div className="absolute -top-40 left-1/2 -translate-x-1/2 w-[900px] h-[900px] rounded-full bg-sky-500/10 blur-3xl pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-6 pt-20 pb-24">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="max-w-3xl">
            <Badge variant="outline" className="border-emerald-400/30 bg-emerald-400/5 text-emerald-300 font-mono text-[10px] tracking-widest mb-6" data-testid="hero-badge">
              <span className="pulse-dot w-1.5 h-1.5 rounded-full bg-emerald-400 mr-2" />
              AI BDRs · LIVE ON 250+ TEAMS
            </Badge>
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-extrabold tracking-tight leading-[0.95]">
              Book pipeline while <br />
              your team <span className="text-sky-400">sleeps</span>.
            </h1>
            <p className="mt-6 text-lg text-slate-300 max-w-2xl leading-relaxed">
              AiPipe deploys autonomous AI BDR agents that research every prospect in real time, run a 7-touch
              multichannel sequence across email + LinkedIn, and hand you booked meetings — not busywork.
            </p>
            <div className="mt-8 flex items-center gap-3">
              <Button size="lg" onClick={cta} data-testid="hero-cta-primary" className="bg-sky-500 hover:bg-sky-400 text-slate-950 font-semibold rounded-full px-7 h-12">
                Start free with Google <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              <a href="#pricing" className="text-sm text-slate-300 hover:text-white transition" data-testid="hero-cta-secondary">See pricing →</a>
            </div>
          </motion.div>

          {/* Stats */}
          <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-4">
            {stats.map((s) => (
              <div key={s.label} className="rounded-2xl border border-white/5 bg-white/[0.02] p-5">
                <div className="text-3xl font-bold text-white">{s.value}</div>
                <div className="mt-1 text-xs uppercase tracking-wider text-slate-500">{s.label}</div>
              </div>
            ))}
          </div>

          {/* Live simulator card */}
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="mt-16 relative">
            <div className="rounded-3xl glass border border-white/10 p-6 md:p-8 shadow-2xl">
              <div className="flex items-center justify-between mb-5">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-rose-400/70" />
                  <div className="w-3 h-3 rounded-full bg-amber-400/70" />
                  <div className="w-3 h-3 rounded-full bg-emerald-400/70" />
                  <span className="ml-3 text-xs text-slate-400 font-mono">agent.session · live</span>
                </div>
                <Badge className="bg-emerald-500/15 text-emerald-300 border-emerald-400/20 font-mono text-[10px]">STREAMING</Badge>
              </div>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="rounded-2xl bg-slate-950/60 border border-white/5 p-5">
                  <div className="text-xs uppercase tracking-widest text-slate-500 mb-3">Prospect enriched · 1.2s</div>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between"><span className="text-slate-400">Name</span><span>Priya Raman</span></div>
                    <div className="flex justify-between"><span className="text-slate-400">Title</span><span>VP Sales · CloudScale</span></div>
                    <div className="flex justify-between"><span className="text-slate-400">Signal</span><span className="text-emerald-300">Raised Series B · 2d ago</span></div>
                    <div className="flex justify-between"><span className="text-slate-400">Intent</span><span className="font-mono">94/100</span></div>
                    <div className="flex justify-between"><span className="text-slate-400">Stack</span><span>Salesforce · Segment</span></div>
                  </div>
                </div>
                <div className="rounded-2xl bg-slate-950/60 border border-white/5 p-5">
                  <div className="text-xs uppercase tracking-widest text-slate-500 mb-3">AI draft · Claude Sonnet</div>
                  <div className="text-sm leading-relaxed text-slate-200">
                    <span className="text-slate-400">Subject:</span> quick thought on your Series B push<br /><br />
                    Hi Priya — congrats on the Series B. Teams scaling from 30 → 90 reps usually hit a pipeline air pocket
                    around month 4. We help you skip it: 3 AI BDRs, live on your Salesforce, booking meetings by Friday.
                    Worth 15 min?
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Logos */}
      <section className="border-y border-white/5 py-8 overflow-hidden">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center text-xs uppercase tracking-widest text-slate-500 mb-6">Trusted by revenue teams shipping pipeline daily</div>
          <div className="relative overflow-hidden">
            <div className="flex gap-12 marquee w-max">
              {[...logos, ...logos].map((l, i) => (
                <div key={i} className="text-slate-500 font-semibold text-lg tracking-wide whitespace-nowrap">{l}</div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="max-w-7xl mx-auto px-6 py-24">
        <div className="max-w-2xl">
          <div className="font-mono text-xs uppercase tracking-widest text-sky-300 mb-3">The platform</div>
          <h2 className="text-4xl lg:text-5xl font-bold">Every job of a BDR — automated end-to-end</h2>
        </div>
        <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {features.map((f) => (
            <div key={f.title} className="group rounded-2xl border border-white/5 bg-white/[0.02] p-6 hover:border-sky-400/30 hover:bg-sky-500/[0.03] transition">
              <div className="w-11 h-11 rounded-xl bg-sky-500/10 grid place-items-center text-sky-300 mb-4 group-hover:scale-105 transition">
                <f.icon className="w-5 h-5" />
              </div>
              <div className="text-lg font-semibold">{f.title}</div>
              <p className="mt-2 text-sm text-slate-400 leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* How it works — 7 touches */}
      <section id="how" className="border-t border-white/5 bg-slate-950/40 py-24">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-end justify-between flex-wrap gap-6 mb-12">
            <div>
              <div className="font-mono text-xs uppercase tracking-widest text-emerald-300 mb-3">Sequence anatomy</div>
              <h2 className="text-4xl lg:text-5xl font-bold max-w-2xl">The 7-touch PLG cadence, adapted per prospect</h2>
            </div>
            <p className="text-slate-400 max-w-md">Each step is rewritten in real time using signals: recent posts, hiring, funding, tech stack.</p>
          </div>
          <div className="grid md:grid-cols-7 gap-3">
            {touches.map((t, i) => (
              <motion.div key={t.day} initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.06 }} className="rounded-xl border border-white/5 bg-white/[0.02] p-4">
                <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500">{t.day}</div>
                <div className="mt-2 text-sm font-semibold">{t.channel}</div>
                <div className="mt-1 text-xs text-slate-400 leading-snug">{t.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" ref={pricingRef} className="max-w-7xl mx-auto px-6 py-24">
        <div className="text-center max-w-3xl mx-auto">
          <div className="font-mono text-xs uppercase tracking-widest text-sky-300 mb-3">Pricing</div>
          <h2 className="text-4xl lg:text-5xl font-bold">Transparent pricing that respects your pipeline goals</h2>
          <p className="mt-4 text-slate-400">Start at $250/mo. Cancel anytime. Most customers see ROI within 30–60 days.</p>
          <div className="mt-8 inline-flex items-center gap-1 rounded-full border border-white/10 p-1 bg-white/[0.03]" data-testid="pricing-billing-toggle">
            {["monthly", "annual"].map((b) => (
              <button key={b} onClick={() => setBilling(b)} data-testid={`pricing-billing-${b}`} className={`px-5 h-9 text-sm rounded-full transition font-medium ${billing === b ? "bg-white text-slate-950" : "text-slate-300 hover:text-white"}`}>
                {b === "monthly" ? "Monthly" : "Annual · Save 20%"}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-14 grid md:grid-cols-3 gap-5">
          {tiers.map((t) => {
            const price = billing === "annual" ? t.annual : t.price;
            return (
              <div key={t.name} className={`relative rounded-3xl p-8 border ${t.popular ? "border-sky-400/50 bg-gradient-to-b from-sky-500/[0.08] to-transparent glow-sky" : "border-white/5 bg-white/[0.02]"}`} data-testid={`pricing-tier-${t.name.toLowerCase()}`}>
                {t.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <Badge className="bg-sky-500 text-slate-950 font-semibold rounded-full">Most popular</Badge>
                  </div>
                )}
                <div className="text-xl font-semibold">{t.name}</div>
                <div className="mt-1 text-sm text-slate-400">{t.tagline}</div>
                <div className="mt-6 flex items-baseline gap-2">
                  <div className="text-5xl font-bold tracking-tight">${price.toLocaleString()}</div>
                  <div className="text-slate-500">/mo</div>
                </div>
                <div className="mt-1 text-xs text-slate-500 font-mono">Billed {billing === "annual" ? "annually" : "quarterly"} · cancel anytime</div>
                <Button onClick={() => subscribe(t)} disabled={loadingTier === t.name} data-testid={`pricing-cta-${t.name.toLowerCase()}`} className={`mt-6 w-full h-11 rounded-full font-semibold ${t.popular ? "bg-sky-500 hover:bg-sky-400 text-slate-950" : "bg-white hover:bg-slate-200 text-slate-900"}`}>
                  {loadingTier === t.name ? "Redirecting…" : `Start with ${t.name}`}
                </Button>
                <div className="mt-7 space-y-3">
                  {t.features.map((f) => (
                    <div key={f} className="flex items-start gap-3 text-sm">
                      <CheckCircle2 className="w-4 h-4 mt-0.5 text-emerald-400 shrink-0" />
                      <span className="text-slate-300">{f}</span>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Proof */}
      <section id="proof" className="border-t border-white/5 bg-slate-950/40 py-24">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-3 gap-5">
            {[
              { icon: TrendingUp, k: "1.8×", v: "More sourced revenue in month one — Metal" },
              { icon: Users, k: "78.95%", v: "LinkedIn connection acceptance rate" },
              { icon: Sparkles, k: "145 hrs", v: "Saved in week one — Mid-Market SaaS" },
            ].map((s) => (
              <div key={s.v} className="rounded-2xl border border-white/5 bg-white/[0.02] p-6">
                <s.icon className="w-5 h-5 text-emerald-300" />
                <div className="mt-4 text-4xl font-bold">{s.k}</div>
                <div className="mt-2 text-sm text-slate-400">{s.v}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="max-w-4xl mx-auto px-6 py-24 text-center">
        <Rocket className="w-8 h-8 mx-auto text-sky-300 mb-4" />
        <h2 className="text-4xl lg:text-5xl font-bold">From sign-up to first meeting in under an hour</h2>
        <p className="mt-4 text-slate-400">Google login → your ICP → live campaign. That fast.</p>
        <div className="mt-8">
          <Button size="lg" onClick={cta} data-testid="footer-cta" className="bg-sky-500 hover:bg-sky-400 text-slate-950 font-semibold rounded-full h-12 px-8">
            Launch my AI BDR <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </section>

      <footer className="border-t border-white/5 py-10 text-center text-xs text-slate-500">
        © 2026 AiPipe · Autonomous pipeline for modern revenue teams
      </footer>
    </div>
  );
}
